import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { CONTACT } from "../i18n";

export const Header = () => {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { id: "services", label: t.nav.services },
    { id: "why", label: t.nav.why },
    { id: "fleet", label: t.nav.fleet },
    { id: "contact", label: t.nav.contact },
  ];

  const scrollTo = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        scrolled
          ? "bg-black/70 backdrop-blur-xl border-b border-white/10 py-4"
          : "bg-transparent py-6"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-between">
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-baseline gap-2 group"
          data-testid="logo-btn"
        >
          <span className="font-serif text-xl md:text-2xl tracking-tight text-white">VIP</span>
          <span className="font-sans text-xs md:text-sm tracking-[0.35em] uppercase text-[#d4af37]">
            Girne Taksi
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => scrollTo(l.id)}
              className="font-sans text-sm text-white/60 hover:text-white transition-colors duration-300 tracking-wide"
              data-testid={`nav-${l.id}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-5">
          <div className="flex items-center gap-2 font-mono text-xs" data-testid="lang-switcher">
            <button
              onClick={() => setLang("tr")}
              className={`transition-colors duration-300 ${lang === "tr" ? "text-[#d4af37]" : "text-white/40 hover:text-white"}`}
              data-testid="lang-tr"
            >
              TR
            </button>
            <span className="text-white/20">/</span>
            <button
              onClick={() => setLang("en")}
              className={`transition-colors duration-300 ${lang === "en" ? "text-[#d4af37]" : "text-white/40 hover:text-white"}`}
              data-testid="lang-en"
            >
              EN
            </button>
          </div>

          <a
            href={CONTACT.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-pill hidden sm:inline-flex items-center rounded-full border border-white/20 px-5 py-2 font-sans text-sm text-white hover:bg-white hover:text-black"
            data-testid="header-whatsapp-btn"
          >
            {t.hero.cta}
          </a>

          <button
            className="md:hidden text-white"
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-btn"
            aria-label="Menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden bg-black/95 backdrop-blur-xl border-t border-white/10 mt-4"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-8 flex flex-col gap-6">
              {links.map((l) => (
                <button
                  key={l.id}
                  onClick={() => scrollTo(l.id)}
                  className="font-serif text-2xl text-white/80 text-left"
                  data-testid={`mobile-nav-${l.id}`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
